import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField
} from "@mui/material";
import { useTesterIdentity } from "./TesterIdentityProvider";
import { reportFeedback } from "./api";

const CATEGORY_OPTIONS = [
  { value: "", label: "(none)" },
  { value: "workflow", label: "Workflow / UX friction" },
  { value: "copy", label: "Copy / wording" },
  { value: "feature-request", label: "Feature request" },
  { value: "other", label: "Other" }
];

const captureClientContext = () => {
  try {
    return {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: navigator.userAgent,
      pathname: window.location.pathname
    };
  } catch {
    return {};
  }
};

const SubmitFeedbackDialog = ({ open, onClose, onSubmitted, hillId }) => {
  const { getIdToken } = useTesterIdentity();
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setDescription("");
    setCategory("");
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Description is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await reportFeedback({
        description: description.trim(),
        category: category || null,
        pageUrl: window.location.href,
        clientContext: captureClientContext(),
        hillId,
        getIdToken
      });
      reset();
      onSubmitted?.(result.feedbackId);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to submit feedback");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send feedback</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="What did you notice?"
            placeholder="Not-a-bug observations — workflow friction, copy that could be clearer, feature requests, anything that felt off"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={4}
            fullWidth
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: focus the field the user is about to type into
            autoFocus
          />
          <TextField
            label="Category (optional)"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            select
            fullWidth
            size="small"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !description.trim()}
        >
          {submitting ? "Sending…" : "Send feedback"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitFeedbackDialog;
